import { type Currency } from '@principle-theorem/accounting';
import { type BillingMeterId } from '../organisation/metered-billing';

/**
 * The tiers an add-on is sold in. Values are persisted on subscription add-ons
 * and mirror the tier names used in Stripe, so do not change them.
 */
export enum AddOnTier {
  Starter = 'starter',
  Growth = 'growth',
  Scale = 'scale',
}

/**
 * One purchasable Stripe price for an add-on.
 *
 * A product carries one entry per variant it sells. Variants always differ by
 * currency, and for the metered products also by tier. `tier` is omitted
 * entirely for add-ons that are not tiered — AI Scribe is a flat per-seat
 * price, so it has two entries that differ only by currency.
 *
 * Prices are a flat list rather than a keyed record because the only lookup
 * performed against them is a reverse scan by `priceId`
 * (`AddOnConfig.findPriceEntry`). A record forced every entry to invent a
 * unique key, which meant encoding the currency into the key as well as the
 * field.
 */
export interface IAddOnPriceConfig {
  priceId: string;
  currency: Currency;
  /**
   * Which tier this price represents, for products sold in tiers. Absent when
   * the add-on is not tiered. Recorded on the subscription add-on purely as
   * provenance — nothing branches on it.
   */
  tier?: AddOnTier;
  /**
   * Links a metered price to the usage meter it bills against. Absent for
   * flat-rate prices.
   */
  billingMeterId?: BillingMeterId;
}

export interface IAddOnConfig {
  productId: string;
  prices: IAddOnPriceConfig[];
}
